import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Avatar,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  FormGrid,
  FormPanel,
  Icon,
  IconButton,
  ListRow,
  RowChip,
  SegmentedControl,
  PageHeader,
  SelectField,
  TableCard,
  Tag,
  TextField,
  Toggle,
  type Column,
} from '@sparkery/ui';
import type {
  AssetFolder,
  DiscountCodeRow,
  RegisterCodeResult,
  ReferralLinkRow,
  TeamMember,
} from '@sparkery/contracts';
import { ApiError, apiPost } from '../../lib/api.ts';
import {
  dateWindow,
  useAssets,
  usePartnerDetail,
  usePartners,
  usePartnerTeam,
} from '../../lib/workspace.ts';
import { useDeleteLink, useInvitePartnerMember } from '../../lib/partner-admin.ts';
import { useGrantFolder, useRevokeFolder } from '../../lib/assets.ts';
import { useViewer } from '../../lib/viewer.tsx';
import { stringParam } from '../route-params.ts';
import { PartnerSettingsCard } from './PartnerSettingsCard.tsx';
import { GenerateLinkForm } from './GenerateLinkForm.tsx';
import { CODE_STATUS_TONE, plural, titleCase } from '../../lib/report.tsx';
import './partners.css';

const PARTNER_DETAIL_TABS = [
  { value: 'details', label: 'Details' },
  { value: 'links', label: 'Links' },
  { value: 'codes', label: 'Codes' },
  { value: 'users', label: 'Users' },
  { value: 'assets', label: 'Assets' },
] as const;

export type PartnerDetailTab = (typeof PARTNER_DETAIL_TABS)[number]['value'];

export function isPartnerDetailTab(value: string): value is PartnerDetailTab {
  return PARTNER_DETAIL_TABS.some((tab) => tab.value === value);
}

/**
 * One partner's links and codes, and the flow that maps a code to them.
 *
 * This is where the code→partner relationship is created. Shopify does not hold it —
 * a discount there is a store-wide object with a code on it and no owner — so this
 * screen is the only place the association exists, and everything downstream reads it.
 */
export function PartnerDetailView() {
  const params: unknown = useParams({ from: '/admin/partners/$partnerId/$tab' });
  const partnerId = stringParam(params, 'partnerId');
  const tab = stringParam(params, 'tab');
  const activeTab = isPartnerDetailTab(tab) ? tab : 'details';
  const [openForm, setOpenForm] = useState<PartnerDetailTab | null>(null);
  const navigate = useNavigate({ from: '/admin/partners/$partnerId/$tab' });
  const detail = usePartnerDetail(partnerId);
  const { can } = useViewer();
  const data = detail.data;

  // The record itself comes from the admin LIST rather than a second detail endpoint:
  // it is the same query the Partners screen already ran, so arriving from a row costs
  // nothing, and there is one place that knows the shape of a partner row.
  const partners = usePartners();
  const partner = partners.data?.partners.find((row) => row.partnerId === partnerId);
  const canManage = can('partners:manage');

  function selectTab(nextTab: PartnerDetailTab) {
    setOpenForm(null);
    void navigate({
      to: '/admin/partners/$partnerId/$tab',
      params: { partnerId, tab: nextTab },
    });
  }

  return (
    <>
      <PageHeader
        title={data?.partnerName ?? partner?.name ?? 'Partner'}
        subtitle={
          partner
            ? [
                partner.archived ? 'Archived' : `${termsLabel(partner)} · ${partner.slug}`,
                `${partner.linkCount} links · ${partner.codeCount} codes`,
              ].join(' · ')
            : 'Referral links and discount codes'
        }
        actions={
          <Link className="btn btnQuiet" to="/admin/partners">
            <Icon name="arrow-left" size={16} />
            All partners
          </Link>
        }
      />

      {detail.isError ? (
        <ErrorState
          title="Could not load this partner"
          {...(detail.error instanceof Error ? { detail: detail.error.message } : {})}
          onRetry={() => void detail.refetch()}
        />
      ) : null}

      <div className="partnerDetailTabs">
        <SegmentedControl
          label="Partner detail section"
          options={[...PARTNER_DETAIL_TABS]}
          value={activeTab}
          onChange={selectTab}
        />
      </div>

      {activeTab === 'details' ? (
        <section className="partnerDetailSection" aria-labelledby="partner-detail-details">
          <h2 id="partner-detail-details" className="partnerDetailTitle">
            Details
          </h2>
          {canManage && partner ? (
            <PartnerSettingsCard partner={partner} />
          ) : (
            <Card>
              <CardHeader title="Partner details" />
              <p className="partnersFormNote">
                You can view this partner, but your role cannot edit their settings.
              </p>
            </Card>
          )}
        </section>
      ) : null}

      {activeTab === 'links' ? (
        <section className="partnerDetailSection" aria-labelledby="partner-detail-links">
          <div className="partnerDetailSectionHeader">
            <h2 id="partner-detail-links" className="partnerDetailTitle">
              Links
            </h2>
            {canManage && partner && !partner.archived ? (
              <Button
                variant={openForm === 'links' ? 'quiet' : 'ink'}
                size="small"
                shape="rounded"
                onClick={() => setOpenForm(openForm === 'links' ? null : 'links')}
                {...(openForm === 'links' ? {} : { icon: 'plus' })}
              >
                {openForm === 'links' ? 'Hide form' : 'Add link'}
              </Button>
            ) : null}
          </div>

          {canManage && partner && openForm === 'links' ? (
            <GenerateLinkForm
              partnerId={partnerId}
              partnerSlug={partner.slug}
              archived={partner.archived}
              onCancel={() => setOpenForm(null)}
            />
          ) : null}

          <TableCard
            title="Referral links"
            meta={data ? plural(data.links.length, 'link') : undefined}
          >
            <DataTable
              caption="Referral links"
              columns={linkColumns}
              rows={data?.links ?? []}
              rowKey={(row) => row.id}
              loading={detail.isPending}
              {...(canManage && !partner?.archived
                ? {
                    rowAction: (row: ReferralLinkRow) => (
                      <span className="partnersRowActions">
                        <IconButton
                          label={`View traffic for ${row.label}`}
                          icon={<Icon name="reporting" size={16} />}
                          onClick={() => void navigate({ to: '/reporting', search: { partnerId } })}
                        />
                        <RemoveLink partnerId={partnerId} link={row} />
                      </span>
                    ),
                  }
                : {})}
              empty={<EmptyState title="No links yet" body="Generate one above." />}
            />
          </TableCard>
        </section>
      ) : null}

      {activeTab === 'codes' ? (
        <section className="partnerDetailSection" aria-labelledby="partner-detail-codes">
          <div className="partnerDetailSectionHeader">
            <h2 id="partner-detail-codes" className="partnerDetailTitle">
              Codes
            </h2>
            {canManage && !partner?.archived ? (
              <Button
                variant={openForm === 'codes' ? 'quiet' : 'ink'}
                size="small"
                shape="rounded"
                onClick={() => setOpenForm(openForm === 'codes' ? null : 'codes')}
                {...(openForm === 'codes' ? {} : { icon: 'plus' })}
              >
                {openForm === 'codes' ? 'Hide form' : 'Add code'}
              </Button>
            ) : null}
          </div>

          {canManage && !partner?.archived && openForm === 'codes' ? (
            <RegisterCodeForm
              partnerId={partnerId}
              partnerName={data?.partnerName ?? ''}
              onCancel={() => setOpenForm(null)}
            />
          ) : null}

          <TableCard title="Discount codes" meta={data ? `${data.codes.length} mapped` : undefined}>
            <DataTable
              caption="Discount codes mapped to this partner"
              columns={codeColumns}
              rows={data?.codes ?? []}
              rowKey={(row) => row.id}
              loading={detail.isPending}
              rowAction={(row) => (
                <span className="partnersRowActions">
                  <IconButton
                    label={`View sales for ${row.code}`}
                    icon={<Icon name="reporting" size={16} />}
                    onClick={() => void navigate({ to: '/reporting', search: { partnerId } })}
                  />
                </span>
              )}
              empty={
                <EmptyState
                  title="No codes mapped yet"
                  body="Orders using an unmapped code earn this partner nothing."
                />
              }
            />
          </TableCard>
        </section>
      ) : null}

      {activeTab === 'users' ? (
        <PartnerUsersTab
          partnerId={partnerId}
          open={openForm === 'users'}
          onToggle={() => setOpenForm(openForm === 'users' ? null : 'users')}
          onCancel={() => setOpenForm(null)}
          canManagePartner={canManage}
        />
      ) : null}

      {activeTab === 'assets' ? (
        <PartnerAssetsTab
          partnerId={partnerId}
          partnerName={data?.partnerName ?? partner?.name ?? 'this partner'}
          open={openForm === 'assets'}
          onToggle={() => setOpenForm(openForm === 'assets' ? null : 'assets')}
          onCancel={() => setOpenForm(null)}
        />
      ) : null}
    </>
  );
}

/**
 * Three states, not two.
 *
 * A partner set to accrue with no rate is a misconfiguration — the commission domain
 * refuses to guess at it and throws — so it is named rather than shown as a
 * legitimate-looking "Fixed fee", which would hide it from the only person who can fix
 * it.
 */
function termsLabel(partner: {
  commissionAccrues: boolean;
  commissionRate: string | null;
}): string {
  if (!partner.commissionAccrues) return 'Fixed fee';
  return partner.commissionRate
    ? `${Number(partner.commissionRate)}% commission`
    : 'Rate missing — commission cannot accrue';
}

function PartnerUsersTab({
  partnerId,
  open,
  onToggle,
  onCancel,
  canManagePartner,
}: {
  partnerId: string;
  open: boolean;
  onToggle: () => void;
  onCancel: () => void;
  canManagePartner: boolean;
}) {
  const team = usePartnerTeam(partnerId);
  const data = team.data;
  const canShowControls = canManagePartner;
  const invite = useInvitePartnerMember(partnerId);

  const columns: Column<TeamMember>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (row) => (
        <span className="partnerDetailPerson">
          <Avatar initials={row.initials} name={row.name} size="sm" />
          <span className="partnerDetailPersonName">
            <strong className="partnerDetailPersonStrong">{row.name}</strong>
            {row.isSelf ? <span className="partnerDetailYou">You</span> : null}
          </span>
        </span>
      ),
    },
    { id: 'email', header: 'Email', secondary: true, cell: (row) => row.email },
    {
      id: 'type',
      header: 'Type',
      cell: (row) => (
        <Tag tone={row.type === 'admin' ? 'strong' : 'neutral'}>
          {row.type === 'admin' ? 'Admin' : 'Standard'}
        </Tag>
      ),
    },
  ];

  return (
    <section className="partnerDetailSection" aria-labelledby="partner-detail-users">
      <div className="partnerDetailSectionHeader">
        <h2 id="partner-detail-users" className="partnerDetailTitle">
          Users
        </h2>
        {canShowControls ? (
          <Button
            variant={open ? 'quiet' : 'ink'}
            size="small"
            shape="rounded"
            onClick={onToggle}
            {...(open ? {} : { icon: 'plus' })}
          >
            {open ? 'Hide form' : 'Invite user'}
          </Button>
        ) : null}
      </div>

      {canShowControls && open ? (
        <InviteUserPanel
          busy={invite.isPending}
          error={invite.error instanceof Error ? invite.error.message : null}
          onCancel={onCancel}
          onInvite={(input) =>
            invite.mutate(input, {
              onSuccess: onCancel,
            })
          }
        />
      ) : null}

      {team.isError ? (
        <ErrorState
          title="Could not load users"
          {...(team.error instanceof Error ? { detail: team.error.message } : {})}
          onRetry={() => void team.refetch()}
        />
      ) : null}

      <TableCard
        title="Users"
        meta={
          data
            ? `${data.members.length} ${data.members.length === 1 ? 'member' : 'members'}`
            : undefined
        }
      >
        <DataTable
          caption="Partner users"
          columns={columns}
          rows={data?.members ?? []}
          rowKey={(row) => row.userId}
          loading={team.isPending}
          {...(canShowControls
            ? {
                rowAction: (row: TeamMember) => (
                  <IconButton
                    label={`Remove ${row.name}`}
                    icon={<Icon name="trash" size={16} />}
                    danger
                    disabled
                  />
                ),
              }
            : {})}
          mobileCard={(row) => (
            <ListRow
              lead={<Avatar initials={row.initials} name={row.name} size="sm" />}
              title={
                <>
                  {row.name}
                  {row.isSelf ? <span className="partnerDetailYou">You</span> : null}
                </>
              }
              meta={row.email}
              trailing={
                <Tag tone={row.type === 'admin' ? 'strong' : 'neutral'}>
                  {row.type === 'admin' ? 'Admin' : 'Standard'}
                </Tag>
              }
              {...(canShowControls
                ? {
                    actions: (
                      <IconButton
                        label={`Remove ${row.name}`}
                        icon={<Icon name="trash" size={16} />}
                        danger
                        disabled
                      />
                    ),
                  }
                : {})}
            />
          )}
          empty={
            <EmptyState title="No users yet" body="Invite the first person for this partner." />
          }
        />
      </TableCard>
    </section>
  );
}

function InviteUserPanel({
  busy,
  error,
  onCancel,
  onInvite,
}: {
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onInvite: (input: { email: string; role: 'partner_user' | 'partner_admin' }) => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'partner_user' | 'partner_admin'>('partner_user');

  return (
    <FormPanel
      title="Invite a user"
      confirmLabel={busy ? 'Sending...' : 'Send invite'}
      confirmDisabled={!email.trim() || busy}
      onCancel={onCancel}
      onConfirm={() => onInvite({ email: email.trim(), role })}
      {...(error ? { note: error } : {})}
    >
      <FormGrid columns="1fr 180px">
        <TextField
          id="partner-user-email"
          label="Email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.currentTarget.value)}
          placeholder="john@bodyline.com"
        />
        <SelectField
          id="partner-user-role"
          label="Role"
          value={role}
          onChange={(event) => setRole(event.currentTarget.value as typeof role)}
          options={[
            { value: 'partner_user', label: 'Standard' },
            { value: 'partner_admin', label: 'Admin' },
          ]}
        />
      </FormGrid>
    </FormPanel>
  );
}

type PartnerAssetRow = {
  folder: AssetFolder;
  visibility: 'Public' | 'Granted';
};

function PartnerAssetsTab({
  partnerId,
  partnerName,
  open,
  onToggle,
  onCancel,
}: {
  partnerId: string;
  partnerName: string;
  open: boolean;
  onToggle: () => void;
  onCancel: () => void;
}) {
  const assets = useAssets();
  const grant = useGrantFolder();
  const revoke = useRevokeFolder();
  const [revokeTarget, setRevokeTarget] = useState<PartnerAssetRow | null>(null);
  const [folderId, setFolderId] = useState('');
  const data = assets.data;
  const canManage = data?.canManage ?? false;

  const rows =
    data?.folders.reduce<PartnerAssetRow[]>((visible, folder) => {
      if (folder.isPublic) {
        visible.push({ folder, visibility: 'Public' });
        return visible;
      }
      if (isGrantedToPartner(folder, partnerId)) {
        visible.push({ folder, visibility: 'Granted' });
      }
      return visible;
    }, []) ?? [];

  const grantable =
    data?.folders.filter((folder) => !folder.isPublic && !isGrantedToPartner(folder, partnerId)) ??
    [];
  const failure = grant.error ?? revoke.error;

  const columns: Column<PartnerAssetRow>[] = [
    {
      id: 'type',
      header: 'Type',
      cell: (row) => (
        <RowChip tint={row.visibility === 'Public' ? 'cyan' : 'amber'} size={28}>
          <Icon name="folder" size={14} />
        </RowChip>
      ),
    },
    {
      id: 'folder',
      header: 'Folder',
      strong: true,
      cell: (row) => row.folder.name,
    },
    {
      id: 'visibility',
      header: 'Visibility',
      cell: (row) => (
        <Tag tone={row.visibility === 'Public' ? 'public' : 'neutral'}>{row.visibility}</Tag>
      ),
    },
  ];

  function grantFolder() {
    if (!folderId) return;
    grant.mutate(
      { folderId, partnerId },
      {
        onSuccess: () => {
          setFolderId('');
          onCancel();
        },
      },
    );
  }

  function revokeAction(row: PartnerAssetRow) {
    if (row.visibility !== 'Granted') return null;

    return (
      <IconButton
        label={`Remove access to ${row.folder.name}`}
        icon={<Icon name="trash" size={16} />}
        danger
        disabled={revoke.isPending}
        onClick={() => setRevokeTarget(row)}
      />
    );
  }

  return (
    <section className="partnerDetailSection" aria-labelledby="partner-detail-assets">
      <div className="partnerDetailSectionHeader">
        <h2 id="partner-detail-assets" className="partnerDetailTitle">
          Assets
        </h2>
        {canManage ? (
          <Button
            variant={open ? 'quiet' : 'ink'}
            size="small"
            shape="rounded"
            disabled={grantable.length === 0 && !open}
            onClick={onToggle}
            {...(open ? {} : { icon: 'plus' })}
          >
            {open ? 'Hide form' : 'Add permission'}
          </Button>
        ) : null}
      </div>

      {canManage && open ? (
        <FormPanel
          title="Grant access to a folder"
          confirmLabel={grant.isPending ? 'Adding…' : 'Add permission'}
          confirmDisabled={!folderId || grant.isPending}
          onCancel={onCancel}
          onConfirm={grantFolder}
          note="Only private folders need granting. Public folders are visible to every partner."
        >
          <FormGrid columns="minmax(0, 260px)">
            <SelectField
              id="partner-asset-folder"
              label="Folder"
              value={folderId}
              onChange={(event) => setFolderId(event.currentTarget.value)}
              options={[
                { value: '', label: 'Choose a folder…' },
                ...grantable.map((folder) => ({ value: folder.id, label: folder.name })),
              ]}
            />
          </FormGrid>
        </FormPanel>
      ) : null}

      {assets.isError ? (
        <ErrorState
          title="Could not load asset permissions"
          {...(assets.error instanceof Error ? { detail: assets.error.message } : {})}
          onRetry={() => void assets.refetch()}
        />
      ) : null}

      {failure ? (
        <p className="partnersFormError" role="alert">
          {failure instanceof Error ? failure.message : 'Could not change folder access.'}
        </p>
      ) : null}

      <ConfirmDialog
        open={revokeTarget !== null}
        title="Remove folder access?"
        body={
          revokeTarget
            ? `Remove ${partnerName}'s access to ${revokeTarget.folder.name}?`
            : undefined
        }
        confirmLabel={revoke.isPending ? 'Removing…' : 'Remove'}
        confirmDisabled={revoke.isPending}
        danger
        onCancel={() => setRevokeTarget(null)}
        onConfirm={() => {
          if (!revokeTarget) return;
          revoke.mutate(
            { folderId: revokeTarget.folder.id, partnerId },
            { onSuccess: () => setRevokeTarget(null) },
          );
        }}
      />

      <TableCard title="Assets" meta={data ? plural(rows.length, 'folder') : undefined}>
        <DataTable
          caption={`Asset folders visible to ${partnerName}`}
          columns={columns}
          rows={rows}
          rowKey={(row) => row.folder.id}
          loading={assets.isPending}
          {...(canManage
            ? {
                rowAction: revokeAction,
              }
            : {})}
          mobileCard={(row) => (
            <ListRow
              lead={
                <RowChip tint={row.visibility === 'Public' ? 'cyan' : 'amber'}>
                  <Icon name="folder" size={14} />
                </RowChip>
              }
              title={row.folder.name}
              meta={`${row.visibility} · ${row.folder.fileCount} ${
                row.folder.fileCount === 1 ? 'file' : 'files'
              }`}
              trailing={
                <Tag tone={row.visibility === 'Public' ? 'public' : 'neutral'}>
                  {row.visibility}
                </Tag>
              }
              {...(canManage && row.visibility === 'Granted'
                ? {
                    actions: revokeAction(row),
                  }
                : {})}
            />
          )}
          empty={
            <EmptyState
              title="No visible folders yet"
              body="Public folders or granted private folders will appear here."
            />
          }
        />
      </TableCard>
    </section>
  );
}

function isGrantedToPartner(folder: AssetFolder, partnerId: string): boolean {
  return folder.grantedTo.some((grantee) => grantee.partnerId === partnerId);
}

/**
 * Removing a link, behind one confirmation click.
 *
 * Not a dialog: the consequence is contained — a link a partner has already published
 * keeps working, because the URL is a plain UTM-tagged page and nothing about it lives
 * in this database. What is lost is the record of having issued it.
 */
function RemoveLink({ partnerId, link }: { partnerId: string; link: ReferralLinkRow }) {
  const [confirming, setConfirming] = useState(false);
  const remove = useDeleteLink(partnerId);

  return (
    <>
      <IconButton
        label={`Remove ${link.label}`}
        icon={<Icon name="trash" size={16} />}
        danger
        onClick={() => setConfirming(true)}
      />
      <ConfirmDialog
        open={confirming}
        title="Remove referral link?"
        body={`Remove ${link.label}? The published URL can still exist, but this portal record will be deleted.`}
        confirmLabel={remove.isPending ? 'Removing…' : 'Remove'}
        confirmDisabled={remove.isPending}
        danger
        onCancel={() => setConfirming(false)}
        onConfirm={() => remove.mutate(link.id, { onSuccess: () => setConfirming(false) })}
      />
    </>
  );
}

/**
 * The registration form.
 *
 * The Shopify check is on by default and the override is a deliberate second action,
 * because the failure it prevents is silent: a mistyped code maps to nothing Shopify
 * will ever send, and the only symptom is commission never arriving.
 */
function RegisterCodeForm({
  partnerId,
  partnerName,
  onCancel,
}: {
  partnerId: string;
  partnerName: string;
  onCancel: () => void;
}) {
  const [code, setCode] = useState('');
  const [allowMissing, setAllowMissing] = useState(false);
  const [amountText, setAmountText] = useState('');
  const queryClient = useQueryClient();

  const register = useMutation({
    mutationFn: () =>
      apiPost<RegisterCodeResult>(`/partners/${partnerId}/codes`, {
        code,
        allowMissingInShopify: allowMissing,
        ...(amountText.trim() ? { amountText: amountText.trim() } : {}),
      }),
    onSuccess: () => {
      setCode('');
      setAmountText('');
      setAllowMissing(false);
      onCancel();
      void queryClient.invalidateQueries({ queryKey: ['partner-detail', partnerId] });
      void queryClient.invalidateQueries({ queryKey: ['partners'] });
    },
  });

  function submit() {
    if (code.trim()) register.mutate();
  }

  const failure = register.error;

  return (
    <FormPanel
      title="Add a discount code"
      confirmLabel={register.isPending ? 'Checking…' : 'Create code'}
      confirmDisabled={!code.trim() || register.isPending}
      onCancel={onCancel}
      onConfirm={submit}
      note={`Shopify holds the discount; this portal holds who it belongs to for ${
        partnerName || 'this partner'
      }.`}
    >
      <FormGrid columns="1fr 1fr 1fr">
        <TextField
          id="register-code"
          label="Code"
          value={code}
          onChange={(event) => setCode(event.target.value.toUpperCase())}
          placeholder="BODY20"
          autoComplete="off"
          spellCheck={false}
        />

        <TextField
          id="register-amount"
          label="Amount"
          value={amountText}
          onChange={(event) => setAmountText(event.target.value)}
          placeholder="20% off entire order"
        />

        <TextField id="register-limit" label="Usage limit" placeholder="10" readOnly />
      </FormGrid>

      <div className="partnerDetailInlineToggle">
        <Toggle
          label="Code does not exist in Shopify yet"
          checked={allowMissing}
          onChange={setAllowMissing}
        />
      </div>

      {failure ? (
        <p className="partnersFormError" role="alert">
          {failure instanceof ApiError ? failure.message : 'Could not map that code.'}
          {failure instanceof ApiError && failure.status === 422 ? (
            <>
              {' '}
              If you are about to create it, turn on{' '}
              <strong>Code does not exist in Shopify yet</strong>.
            </>
          ) : null}
        </p>
      ) : null}

      {register.data ? (
        <p className="partnersFormOk" role="status">
          <strong>{register.data.code.code}</strong> now maps to {register.data.partnerName}
          {register.data.verification === 'verified' && register.data.shopifyTitle
            ? ` — Shopify calls it “${register.data.shopifyTitle}”.`
            : register.data.verification === 'unchecked'
              ? ' — Shopify is not configured here, so it was saved unverified.'
              : ' — saved without a Shopify match, as you confirmed.'}
        </p>
      ) : null}
    </FormPanel>
  );
}

const codeColumns: Column<DiscountCodeRow>[] = [
  {
    id: 'code',
    header: 'Discount code',
    cell: (row) => (
      <span className="referCode">
        <strong>{row.code}</strong>
        <span className="referCodeWindow">{dateWindow(row.startsAt, row.endsAt)}</span>
      </span>
    ),
  },
  { id: 'amount', header: 'Amount', secondary: true, cell: (row) => row.amountText },
  {
    id: 'status',
    header: 'Status',
    cell: (row) => <Tag tone={CODE_STATUS_TONE[row.status]}>{titleCase(row.status)}</Tag>,
  },
  {
    id: 'usage',
    header: 'Usage',
    numeric: true,
    cell: (row) => `${row.used}/${row.usageLimit ?? '∞'}`,
  },
];

const linkColumns: Column<ReferralLinkRow>[] = [
  { id: 'label', header: 'Label', cell: (row) => row.label },
  { id: 'url', header: 'Link', cell: (row) => <span className="referUrl">{row.url}</span> },
  {
    id: 'channel',
    header: 'Channel',
    secondary: true,
    cell: (row) => row.channel + (row.placement ? ` · ${row.placement}` : ''),
  },
];
